import type { ReminderScheduleResponseDto } from '@trellis/api'
import { ConfirmDialog } from '@trellis/roots/overlays'

const pluralise = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`

/**
 * Whether switching a schedule off changes anything today: the default and a client's own
 * schedule apply automatically, and any other one matters once a draft invoice or
 * recurring invoice has picked it or reminders are already scheduled from it.
 */
export function isReminderScheduleInUse(
  schedule: Pick<ReminderScheduleResponseDto, 'isDefault' | 'clientId' | 'usage'>,
): boolean {
  const { draftInvoices, recurringInvoices, scheduledReminders } = schedule.usage
  return (
    schedule.isDefault ||
    schedule.clientId !== null ||
    draftInvoices + recurringInvoices + scheduledReminders > 0
  )
}

/**
 * What stops when a schedule is off. Invoices only get reminders when they are emailed,
 * and an off schedule is not replaced by another one, so reminders already scheduled are
 * the one thing that carries on.
 */
export function describeReminderScheduleDisableEffects(
  schedule: Pick<
    ReminderScheduleResponseDto,
    'isDefault' | 'clientId' | 'clientName' | 'usage'
  >,
): { consequences: string[]; unaffected: string | null } {
  const { draftInvoices, recurringInvoices, scheduledReminders } = schedule.usage
  const consequences: string[] = []

  if (schedule.isDefault) {
    consequences.push(
      "It's the default, so it applies to every new invoice unless the invoice or its client uses another schedule.",
    )
  }
  if (schedule.clientId !== null) {
    const owner = schedule.clientName ? `${schedule.clientName}'s` : "this client's"
    consequences.push(
      `It's ${owner} own schedule, so invoices emailed to them won't get reminders.`,
    )
  }
  if (draftInvoices > 0) {
    consequences.push(
      `${pluralise(draftInvoices, 'draft invoice')} ${draftInvoices === 1 ? 'uses' : 'use'} it and won't get reminders once emailed.`,
    )
  }
  if (recurringInvoices > 0) {
    consequences.push(
      `${pluralise(recurringInvoices, 'recurring invoice')} ${recurringInvoices === 1 ? 'uses' : 'use'} it, so the invoices ${recurringInvoices === 1 ? 'it creates' : 'they create'} won't get reminders.`,
    )
  }

  return {
    consequences,
    unaffected:
      scheduledReminders > 0
        ? `${pluralise(scheduledReminders, 'reminder')} already scheduled on sent invoices will still go out. Stop them from each invoice if you don't want them sent.`
        : null,
  }
}

interface ReminderScheduleDisableDialogProps {
  schedule: ReminderScheduleResponseDto
  open: boolean
  onOpenChange: (isOpen: boolean) => void
  isPending?: boolean
  onConfirm: () => void
}

/** Asks before an in-use schedule is switched off, spelling out what stops. */
export function ReminderScheduleDisableDialog({
  schedule,
  open,
  onOpenChange,
  isPending = false,
  onConfirm,
}: ReminderScheduleDisableDialogProps) {
  const { consequences, unaffected } = describeReminderScheduleDisableEffects(schedule)

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Switch off ${schedule.clientName ?? schedule.name}?`}
      description={
        <div className="flex flex-col gap-2.5">
          <p>
            While it&apos;s off, invoices that use it won&apos;t get any payment
            reminders. They don&apos;t fall back to another schedule.
          </p>
          {consequences.length > 0 && (
            <ul className="list-disc space-y-1 pl-5">
              {consequences.map((consequence) => (
                <li key={consequence}>{consequence}</li>
              ))}
            </ul>
          )}
          {unaffected && (
            <p>
              <span className="text-foreground font-medium">Not affected:</span>{' '}
              {unaffected}
            </p>
          )}
          <p>
            You can switch it back on at any time, but invoices emailed while it&apos;s
            off won&apos;t get reminders afterwards.
          </p>
        </div>
      }
      confirmLabel="Switch off"
      cancelLabel="Keep it on"
      isPending={isPending}
      closeOnConfirm={false}
      onConfirm={onConfirm}
    />
  )
}
